import { IframeProvider, type IIframeProvider } from "../modal/IframeProvider";
import type { CalculatorData } from "../modal/types";
import { ConfigClient, type IConfigClient } from '../config/ConfigClient';
import { type Config, ContentContainer, type Options } from "./types";
import { PostMessageHandler } from '../dom/PostMessageHandler';
import { ModalCoordinator } from "../modal/ModalCoordinator";
import { WidgetFactory } from "../widgets/WidgetFactory";
import { ContentScanner } from '../dom/ContentScanner';
import { BaseWidget } from "../widgets/BaseWidget";
import { GtmLoader } from "../tracking/GtmLoader";
import { MixpanelLoader } from "../tracking/MixpanelLoader";
import { GoogleAnalyticsService } from "../tracking/GoogleAnalyticsService";
import { MixpanelService } from "../tracking/MixpanelService";
import { TrackingOrchestrator } from "../tracking/TrackingOrchestrator";
import type { ITrackingService } from "../tracking/ITrackingService";
import { version } from '../../package.json';
import { Logger, LogLevel } from './Logger';

export class SelectfiWidgetSdk {
  public static readonly version: string = version;
  private static readonly selectfiGtmId: string = "GTM-PTS5J97R"; // Container "Widget SDK"
  private config: Config | null = null;
  private tracker: ITrackingService | null = null;

  private containers: ContentContainer[] = [];
  private widgetMapping: Map<string, BaseWidget> = new Map();

  private constructor(
    private readonly options: Options,
    private readonly configClient: IConfigClient,
    private readonly contentScanner: ContentScanner,
  ) { }

  public static async create(options: Options): Promise<SelectfiWidgetSdk> {

    const configClient = new ConfigClient(options.environment);
    const contentScanner = new ContentScanner();

    if (options.testMode) {
      Logger.setLevel(LogLevel.DEBUG);
    }

    const sdk = new SelectfiWidgetSdk(
      options,
      configClient,
      contentScanner
    );

    await sdk.initialize();
    return sdk;
  }

  // Used when content is dynamicly added after initial load (e.g. scrolling down on DDC SRP pages)
  public rescan(): void {
    if (!this.config) {
      Logger.warn('Cannot rescan and hydrate containers: config not loaded yet');
      return;
    }
    const newContainers = this.contentScanner.scan(this.options.dealerMap !== undefined);

    newContainers.forEach(container => {
      const alreadyHydrated = this.containers.some(c => c.element === container.element);
      if (alreadyHydrated) {
        return;
      }

      // Add to our tracked containers
      this.containers.push(container);

      if (this.tracker && this.config) {
        this.hydrateContainer(container, this.config, this.tracker);
      }
    });
  }

  public debug(): void {
    Logger.setLevel(LogLevel.DEBUG);
    Logger.debug('Debug logging enabled');
  }

  private async initialize(): Promise<void> {
    if (!this.options.testMode) {
      GtmLoader.load(SelectfiWidgetSdk.selectfiGtmId);
      MixpanelLoader.load();
    }

    try {
      Logger.debug(`SelectFiWidget initializing with version v${SelectfiWidgetSdk.version}`);

      // Async: fetch config, then hydrate
      return await this.configClient
        .fetchConfig(this.options.widgetKey)
        .then((config) => this.onConfigLoaded(config))
        .catch((error) => Logger.error('Config fetch failed', error));
    } catch (error) {
      Logger.error('Failed to initialize SelectFi Widget SDK', error);
    }
  }

  private onConfigLoaded(config: Config): void {
    this.config = config;
    try {
      if (!config.enabled) {
        Logger.warn('Widget disabled by SelectFI configuration');
        return;
      }

      const googleAnalyticsService = new GoogleAnalyticsService(config, this.options.testMode);
      const mixpanelService = new MixpanelService(config, this.options.testMode, this.options.isDebug ?? false);
      this.tracker = new TrackingOrchestrator(googleAnalyticsService, mixpanelService);
      this.tracker.initialize();

      // Initialize ModalCoordinator singleton
      ModalCoordinator.initialize(config, this.tracker);

      Logger.debug('Config loaded, setting up post message handler');
      const postMessageHandler = new PostMessageHandler(this.tracker);
      postMessageHandler.attatchListener();

      // Scan for containers now that config is ready
      this.containers = this.contentScanner.scan(this.options.dealerMap !== undefined);
      Logger.debug(`Found ${this.containers.length} containers`);

      Logger.debug(`Hydrating ${this.containers.length} containers`);
      for (const container of this.containers) {
        this.hydrateContainer(container, config, this.tracker);
      }

      // Start observing for new containers added after initial hydration
      this.setupMutationObserver();
    } catch (error) {
      Logger.error('Failed to load config', error);
    }
  }

  private hydrateContainer(container: ContentContainer, config: Config, tracker: ITrackingService): void {
    try {
      const dealerKey = this.resolveDealerKey(container);
      if (!dealerKey) return;

      const trackerId = PostMessageHandler.registerContainer(
        container,
        (trackerId, calculatorData) => this.handleSwapToScreening(trackerId, calculatorData)
      );

      const iframeProvider: IIframeProvider = new IframeProvider(
        trackerId,
        this.options.environment,
      );

      const widget = WidgetFactory.create(
        container,
        dealerKey,
        config,
        iframeProvider,
        tracker,
        this.options,
        trackerId
      );

      this.widgetMapping.set(trackerId, widget);
      widget.inject();
    } catch (error) {
      Logger.error('Failed to hydrate container', error);
    }
  }

  private handleSwapToScreening(trackerId: string, calculatorData?: CalculatorData): void {
    const widget = this.widgetMapping.get(trackerId);
    if (widget) {
      widget.swapCalculatorForScreening(calculatorData);
    } else {
      Logger.error('No widget found for trackerId:', trackerId);
    }
  }

  private resolveDealerKey(container: ContentContainer): string | null {
    if (!this.options.dealerMap) {
      return this.options.widgetKey;
    }

    if (!container.dealerName) {
      Logger.error('Container missing data-selectfi-dealer-name', container.element);
      return null;
    }

    const dealerKey = this.options.dealerMap[container.dealerName];
    if (!dealerKey) {
      Logger.error(`Unknown dealer: ${container.dealerName}`, container.element);
      return null;
    }

    return dealerKey;
  }

  private setupMutationObserver(): void {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          // Check if the added node itself is a SelectFi container
          if (node.dataset.selectfiProduct !== undefined) {
            Logger.debug('New SelectFi container detected, hydrating...');
            const newContentContainer = ContentContainer.createFromElement(node, this.options.dealerMap !== undefined);
            this.hydrateContainer(newContentContainer, this.config!, this.tracker!);
            return;
          }

          // Check if the added node contains any SelectFi containers as descendants
          const nestedContainers = node.querySelectorAll<HTMLElement>('[data-selectfi-product]');
          nestedContainers.forEach((container) => {
            Logger.debug('Nested SelectFi container detected, hydrating...');
            const newContentContainer = ContentContainer.createFromElement(container, this.options.dealerMap !== undefined);
            this.hydrateContainer(newContentContainer, this.config!, this.tracker!);
          });
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}
